import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import type Owner from "@ember/owner";
import Service, { service } from "@ember/service";
import Coordinator from "parog-web/services/-private/coordinator";
import { resolve } from "rsvp";
/**
 * The FetcherService is responsible for fetching data from the API.
 * It is for now a fork of Ember Data Storefront.
 */
export default class FetcherService extends Service {
  @service declare store: Store;

  private coordinator: Coordinator;

  constructor(properties?: Owner) {
    super(properties);
    this.coordinator = new Coordinator(this.store);
  }

  /**
   * `loadRecord` can be used in place of `store.findRecord` to fetch a single record for the given type, id and options.
   *
   * ```diff
   * this.get('store')
   * -   .findRecord('post', 1, { include: 'comments' })
   * +   .loadRecord('post', 1, { include: 'comments' })
   * .then(post => post);
   * ```
   *
   * `loadRecord` caches based on the query you provide, so each of the following examples would return a blocking promise the first time they are called, and synchronously resolve from the cache thereafter.
   *
   * ```js
   * // simple fetch
   * this.get('store').loadRecord('post', 1);
   *
   * // includes
   * this.get('store').loadRecord('post', 1, { include: 'comments' });
   * ```
   *
   * This solves many common bugs where `findRecord` would return immediately, even if important `includes` had never been loaded.
   *
   * Similar to `store.findRecord`, you can force a query to reload using `reload: true`:
   *
   * ```
   * // force an already loaded set to reload (blocking promise)
   * store.loadRecord('post', 1, { reload: true });
   * ```
   *
   * In most cases, `loadRecord` should be a drop-in replacement for `findRecord` that eliminates bugs and improves your app's caching.
   *
   * @method loadRecord
   * @param type type of model to load
   * @param id id of model to load
   * @param options (optional) a hash of options
   * @return a promise resolving with the record array
   * @public
   */
  loadRecord<T extends keyof ModelRegistry>(
    type: T,
    id: string,
    options: Record<string, unknown> = {}
  ): Promise<ModelRegistry[T]> {
    const query = this.coordinator.recordQueryFor(type, id, options);
    const shouldBlock = options.reload || !query.value;
    const shouldBackgroundReload =
      options.backgroundReload !== undefined ? options.backgroundReload : true;
    let promise: Promise<ModelRegistry[T]>;

    if (shouldBlock) {
      promise = query.run();
    } else {
      promise = resolve(query.value);

      if (shouldBackgroundReload) {
        query.run();
      }
    }

    return promise;
  }

  /**
   * `loadRecords` can be used in place of `store.query` to fetch a collection of records for the given type and options.
   *
   * ```diff
   * this.get('store')
   * -   .query('post', { filter: { popular: true } })
   * +   .loadRecords('post', { filter: { popular: true } })
   * .then(models => models);
   * ```
   *
   * `loadRecords` caches based on the query you provide, so each of the following examples would return a blocking promise the first time they are called, and instantly resolve from the cache thereafter.
   *
   * ```js
   * // filters
   * store.loadRecords('post', { filter: { popular: true }});
   *
   * // pagination
   * store.loadRecords('post', { page: { limit: 10, offset: 0 }});
   *
   * // includes
   * store.loadRecords('post', { include: 'comments' });
   *
   * // force an already loaded set to reload (blocking promise)
   * store.loadRecords('post', { reload: true });
   * ```
   *
   * In most cases, `loadRecords` should be a drop-in replacement for `query` that eliminates bugs and improves your app's caching.
   */
  async loadRecords<T extends keyof ModelRegistry>(
    type: T,
    options: Record<string, unknown> = {}
  ): Promise<Array<ModelRegistry[T]>> {
    const query = this.coordinator.recordArrayQueryFor(type, options);
    const shouldBlock = options.reload || !query.value;
    const shouldBackgroundReload =
      options.backgroundReload !== undefined ? options.backgroundReload : true;
    let promise: Promise<Array<ModelRegistry[T]>>;
    let fetcher: Promise<Array<ModelRegistry[T]>>;

    if (shouldBlock) {
      promise = query.run();
      fetcher = promise;
    } else {
      promise = resolve(query.value);

      fetcher = shouldBackgroundReload ? query.run() : resolve();
    }

    await fetcher;
    query.trackIncludes();

    return promise;
  }

  query<K extends keyof ModelRegistry>(
    modelName: K,
    query: object,
    options?: { adapterOptions?: object | undefined }
  ) {
    return this.store.query(modelName, query, options);
  }
}

declare module "@ember/service" {
  interface Registry {
    fetcher: FetcherService;
  }
}
